import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { EditStatusType, StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { ClearThirdPartyAddress, SetThirdPartyAddresses } from './address.actions';
import { ContractObjectDefaults } from './contract-object.defaults';
import { EntityNodeType, PartyNodeType } from './contract-object.types';
import { UpdateContract } from './contract.actions';
import { ContractState } from './contract.state';
import {
    AddConsumer,
    AddEntityAddressLink,
    AddEntityPhoneLink,
    AddThirdPartyEntity,
    DeleteThirdPartyEntity,
    RemoveEntityAddressLink,
    RemoveEntityPhoneLink,
    SetRetailBroker,
    UpdateEntity
} from './entity.actions';
import { AddEntityInterestLink, UpdateInterest } from './interest.actions';
import { AddParty } from './party.actions';
import { PartyState } from './party.state';
import { ClearThirdPartyPhone, SetThirdPartyPhones } from './phone.actions';

export interface EntityStateModel {
    entity: any;
    currentConsumerID: string;
}

@State<EntityStateModel>({
    name: 'entityState',
    defaults: {
        entity: {},
        currentConsumerID: null
    }
})
@Injectable()
export class EntityState {
    constructor(private store: Store, private helper: StateHelperService, private app: AppService) { }

    @Selector()
    static consumer(state: EntityStateModel) {
        return state.entity[state.currentConsumerID] || {};
    }

    @Selector()
    static currentConsumerID(state: EntityStateModel) {
        return state.currentConsumerID;
    }

    @Selector()
    static consumerComments(state: EntityStateModel) {
        let comments: any = [];
        let consumerRole = state.entity[state.currentConsumerID].Consumer_Role;
        if (consumerRole.Entity_Comments && consumerRole.Entity_Comments.Entity_Comment)
            for (var i in consumerRole.Entity_Comments.Entity_Comment) {
                comments.push(consumerRole.Entity_Comments.Entity_Comment[i]);
            }
        return comments;
    }

    @Selector()
    static entityByID(state: EntityStateModel) {
        return (entityID: string) => {
            let result: any = {};
            if (state.entity[entityID]) {
                result = state.entity[entityID];
            }
            return result;
        };
    }

    @Action(AddConsumer)
    addConsumer(sc: StateContext<EntityStateModel>, action: AddConsumer) {
        const state = sc.getState();
        let entityID = action.consumer.Entity_ID;

        sc.patchState({
            entity: {
                ...state.entity,
                [entityID]: action.consumer
            },
            currentConsumerID: entityID
        });
    }

    @Action(AddEntityAddressLink)
    addEntityAddressLink(sc: StateContext<EntityStateModel>, action: AddEntityAddressLink) {
        const state = sc.getState();

        let entityID = action.entityID;

        let updateEntity: any = cloneDeep(state.entity[entityID]);
        this.helper.setItemChanged(updateEntity, updateEntity.Edit_Status);

        if (!updateEntity.Addresses) {
            updateEntity.Addresses = {};
        }

        if (!updateEntity.Addresses.Address) {
            updateEntity.Addresses.Address = [];
        }

        updateEntity.Addresses.Address = [...updateEntity.Addresses.Address, action.addressID];

        sc.patchState({
            entity: {
                ...state.entity,
                [entityID]: { ...state.entity[entityID], ...updateEntity }
            }
        });

        if (updateEntity.nodeType == EntityNodeType.Third_Party) {
            this.store.dispatch(new UpdateInterest({ Interest_ID: updateEntity.Interest_ID }));
        }
    }

    @Action(RemoveEntityAddressLink)
    removeEntityAddressLink(sc: StateContext<EntityStateModel>, action: RemoveEntityAddressLink) {
        const state = sc.getState();

        let entityID = action.entityID;

        let updateEntity: any = cloneDeep(state.entity[entityID]);
        this.helper.setItemChanged(updateEntity, updateEntity.Edit_Status);

        if (!updateEntity.Addresses) {
            updateEntity.Addresses = {};
        }

        if (!updateEntity.Addresses.Address) {
            updateEntity.Addresses.Address = [];
        }

        if (action.addressID < 0) {
            updateEntity.Addresses.Address = this.helper.removeArrayItemByValue(
                updateEntity.Addresses.Address,
                action.addressID
            );
        }

        sc.patchState({
            entity: {
                ...state.entity,
                [entityID]: { ...state.entity[entityID], ...updateEntity }
            }
        });

        if (updateEntity.nodeType == EntityNodeType.Third_Party) {
            this.store.dispatch(new UpdateInterest({ Interest_ID: updateEntity.Interest_ID }));
        }
    }

    @Action(AddEntityPhoneLink)
    addEntityPhoneLink(sc: StateContext<EntityStateModel>, action: AddEntityPhoneLink) {
        const state = sc.getState();

        let entityID = action.entityID;
        if (!entityID) {
            entityID = state.currentConsumerID;
        }
        let updateEntity: any = cloneDeep(state.entity[entityID]);
        this.helper.setItemChanged(updateEntity, updateEntity.Edit_Status);

        if (!updateEntity.Phones) {
            updateEntity.Phones = {};
        }

        if (!updateEntity.Phones.Phone) {
            updateEntity.Phones.Phone = [];
        }

        updateEntity.Phones.Phone = [...updateEntity.Phones.Phone, action.phoneID];

        sc.patchState({
            entity: {
                ...state.entity,
                [entityID]: { ...state.entity[entityID], ...updateEntity }
            }
        });

        if (updateEntity.nodeType == EntityNodeType.Third_Party) {
            this.store.dispatch(new UpdateInterest({ Interest_ID: updateEntity.Interest_ID }));
        }
    }

    @Action(RemoveEntityPhoneLink)
    removeEntityPhoneLink(sc: StateContext<EntityStateModel>, action: RemoveEntityPhoneLink) {
        const state = sc.getState();

        let entityID = action.entityID;
        if (!entityID) {
            entityID = state.currentConsumerID;
        }
        let updateEntity: any = cloneDeep(state.entity[entityID]);
        this.helper.setItemChanged(updateEntity, updateEntity.Edit_Status);

        if (!updateEntity.Phones) {
            updateEntity.Phones = {};
        }

        if (!updateEntity.Phones.Phone) {
            updateEntity.Phones.Phone = [];
        }

        if (action.phoneID < 0) {
            updateEntity.Phones.Phone = this.helper.removeArrayItemByValue(updateEntity.Phones.Phone, action.phoneID);
        }

        sc.patchState({
            entity: {
                ...state.entity,
                [entityID]: { ...state.entity[entityID], ...updateEntity }
            }
        });

        if (updateEntity.nodeType == EntityNodeType.Third_Party) {
            this.store.dispatch(new UpdateInterest({ Interest_ID: updateEntity.Interest_ID }));
        }
    }

    @Action(AddThirdPartyEntity)
    addThirdPartyEntity(sc: StateContext<EntityStateModel>, action: AddThirdPartyEntity) {
        const state = sc.getState();
        let entityID: any = action.entity.Entity_ID;
        let interestID: string = action.interestID;
        let newEntity = this.helper.createDefaultItem(ContractObjectDefaults, 'Interest_Name', action.entity);

        if (parseInt(entityID, 10) < 0) {
            this.helper.setItemAdded(newEntity);
        }

        newEntity.Interest_ID = interestID;

        if (!newEntity.TP_Book) {
            newEntity.TP_Book = {};
        }
        newEntity.TP_Book.Entity_ID = this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID;

        if (newEntity.Addresses && newEntity.Addresses.Address) {
            let updateStateAddress = cloneDeep(newEntity.Addresses.Address);
            this.store.dispatch(new SetThirdPartyAddresses(updateStateAddress));

            for (var i in newEntity.Addresses.Address) {
                newEntity.Addresses.Address[i] = newEntity.Addresses.Address[i].Address_ID;
            }
        }

        if (newEntity.Phones && newEntity.Phones.Phone) {
            let updateStatePhone = cloneDeep(newEntity.Phones.Phone);
            this.store.dispatch(new SetThirdPartyPhones(updateStatePhone));

            for (var i in newEntity.Phones.Phone) {
                newEntity.Phones.Phone[i] = newEntity.Phones.Phone[i].Phone_ID;
            }
        }

        if (newEntity.Roles) {
            delete newEntity.Roles;
        }

        if (!newEntity.nodeType) {
            newEntity.nodeType = EntityNodeType.Third_Party;
        }

        if (newEntity.Org_Entity_Name) {
            newEntity.Entity_Type = 'O';
            delete newEntity.Org_Entity_Name;
        } else {
            newEntity.Entity_Type = 'I';
        }

        sc.patchState({
            entity: { ...state.entity, [entityID]: newEntity }
        });

        // Dispatch link for new entity to interest
        this.store.dispatch(new AddEntityInterestLink(entityID, interestID));
    }

    @Action(UpdateEntity)
    updateEntity(sc: StateContext<EntityStateModel>, action: UpdateEntity) {
        const state = sc.getState();
        const entityID = action.entity.Entity_ID;
        let currentEntity = cloneDeep(state.entity[entityID]);
        let updateEntity: any = cloneDeep(action.entity);

        updateEntity = { ...currentEntity, ...updateEntity }; //merge the data from current and the updated data passed in.

        this.helper.setItemChanged(updateEntity, currentEntity.Edit_Status);
        if (updateEntity.nodeType && updateEntity.nodeType == EntityNodeType.Consumer) {
            if (!updateEntity.Consumer_Role) {
                updateEntity.Consumer_Role = {};
            }
            updateEntity.Consumer_Role.Edit_Status = EditStatusType.Change;
        }

        if (updateEntity.Entity_Type != currentEntity.Entity_Type) {
            if (updateEntity.Entity_Type == 'O') {
                if (currentEntity.Indv_Name) {
                    if (currentEntity.nodeType == EntityNodeType.Third_Party) {
                        updateEntity.Entity_Name = this.combineIndvName(
                            currentEntity.Indv_Name.Indv_First_Name,
                            currentEntity.Indv_Name.Indv_Middle_Name,
                            currentEntity.Indv_Name.Indv_Last_Name
                        );
                    } else {
                        updateEntity.Org_Entity_Name = this.combineIndvName(
                            currentEntity.Indv_Name.Indv_First_Name,
                            currentEntity.Indv_Name.Indv_Middle_Name,
                            currentEntity.Indv_Name.Indv_Last_Name
                        );
                    }
                    updateEntity.Indv_Name.Indv_First_Name = '';
                    updateEntity.Indv_Name.Indv_Middle_Name = '';
                    updateEntity.Indv_Name.Indv_Last_Name = '';
                }
            } else {
                let nameData;
                if (currentEntity.Org_Entity_Name) {
                    nameData = currentEntity.Org_Entity_Name.split(' ');
                } else {
                    nameData = currentEntity.Entity_Name.split(' ');
                }

                switch (true) {
                    case nameData.length == 1:
                        updateEntity.Indv_Name.Indv_Last_Name = nameData.pop();
                        break;

                    case nameData.length == 2:
                        updateEntity.Indv_Name.Indv_Last_Name = nameData.pop();
                        updateEntity.Indv_Name.Indv_First_Name = nameData.pop();

                        break;

                    case nameData.length > 2:
                        updateEntity.Indv_Name.Indv_Last_Name = nameData.pop();
                        updateEntity.Indv_Name.Indv_First_Name = nameData.shift();

                        let middleName: string = '';
                        for (var i in nameData) {
                            middleName += ' ' + nameData[i];
                        }

                        if (middleName) {
                            middleName = middleName.trim();
                            updateEntity.Indv_Name.Indv_Middle_Name = middleName;
                        }

                        break;
                    default:
                        console.log('Error parsing Entity Name');
                        break;
                }
            }
        }

        //Update combined Entity_Name from Indv_Name data or Org_Entity_Name.
        if (updateEntity.Entity_Type == 'I' && updateEntity.Indv_Name) {
            updateEntity.Entity_Name = this.combineIndvName(
                updateEntity.Indv_Name.Indv_First_Name,
                updateEntity.Indv_Name.Indv_Middle_Name,
                updateEntity.Indv_Name.Indv_Last_Name
            );
        }

        if (updateEntity.Entity_Type == 'O' && updateEntity.nodeType == EntityNodeType.Consumer) {
            updateEntity.Entity_Name = updateEntity.Org_Entity_Name;
        }

        if (
            updateEntity.nodeType == EntityNodeType.Consumer &&
            updateEntity.Consumer_Role?.Producer?.Entity_ID &&
            updateEntity.Consumer_Role?.Producer?.Entity_ID != currentEntity.Consumer_Role?.Producer?.Entity_ID
        ) {
            let producerParties = this.store.selectSnapshot(PartyState.producerPartyCount);
            if (producerParties <= 0) {
                this.store.dispatch(
                    new AddParty(PartyNodeType.Contract_Producer, {
                        Producer: {
                            Entity_ID: updateEntity.Consumer_Role.Producer.Entity_ID,
                            Entity_Name: updateEntity.Consumer_Role.Producer.Entity_Name
                        }
                    })
                );
            }
        }

        // Update Operations_Desc on Contract if it is empty when Business_Desc is updated on Consumer
        if (this.helper.propertyChanged(state.entity[entityID], updateEntity, 'Business_Desc')) {
            let contract = { ...this.store.selectSnapshot(ContractState.contract) };
            if (updateEntity.Business_Desc && !contract.Operations_Desc) {
                contract.Operations_Desc = updateEntity.Business_Desc;
                this.store.dispatch([new UpdateContract(contract)]);
            }
        }

        sc.patchState({
            entity: {
                ...state.entity,
                [entityID]: updateEntity
            }
        });

        if (updateEntity.nodeType == EntityNodeType.Third_Party) {
            this.store.dispatch(new UpdateInterest({ Interest_ID: updateEntity.Interest_ID }));
        }
    }

    @Action(DeleteThirdPartyEntity)
    deleteThirdPartyEntity(sc: StateContext<EntityStateModel>, action: DeleteThirdPartyEntity) {
        const state = sc.getState();
        let entity = cloneDeep(state.entity);
        let currentEntity = entity[action.entityID];

        if ((currentEntity.Edit_Status = EditStatusType.Add)) {
            if (currentEntity.Addresses && currentEntity.Addresses.Address) {
                for (var i in currentEntity.Addresses.Address) {
                    this.store.dispatch(new ClearThirdPartyAddress(currentEntity.Addresses.Address[i]));
                }
            }

            if (currentEntity.Phones && currentEntity.Phones.Phone) {
                for (var i in currentEntity.Phones.Phone) {
                    this.store.dispatch(new ClearThirdPartyPhone(currentEntity.Phones.Phone[i]));
                }
            }
        }

        delete entity[action.entityID];

        sc.patchState({ entity: entity });
    }

    combineIndvName(first: string, middle: string, last: string): string {
        let combinedName: string = '';
        if (first) {
            first = first.trim();
        }

        if (middle) {
            middle = middle.trim();
        }

        if (last) {
            last = last.trim();
        }

        if (first && first != '') {
            combinedName = first;
        }
        if (middle && middle != '') {
            combinedName += ' ' + middle;
        }
        if (last && last != '') {
            combinedName += ' ' + last;
        }

        combinedName = combinedName.trim();

        return combinedName;
    }

    @Action(SetRetailBroker)
    setRetailBroker(sc: StateContext<EntityStateModel>, action: SetRetailBroker) {
        const state = sc.getState();
        let entityID = action.entityID;
        let updateEntity: any = cloneDeep(state.entity[entityID]);
        if (
            !updateEntity.Consumer_Role.Broker ||
            (updateEntity.Consumer_Role.Broker && !(updateEntity.Consumer_Role.Broker.Entity_ID > 0))
        ) {
            this.helper.setItemChanged(updateEntity, updateEntity.Edit_Status);
            this.helper.setItemChanged(updateEntity.Consumer_Role, updateEntity.Consumer_Role.Edit_Status);
            updateEntity.Consumer_Role.Broker = {
                Entity_ID: action.broker.Entity_ID,
                Entity_Name: action.broker.Entity_Name
            };
            sc.patchState({
                entity: {
                    ...state.entity,
                    [entityID]: { ...state.entity[entityID], ...updateEntity }
                }
            });
        }
    }
}
